import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, Subject, combineLatest, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { CodeDescriptionError, ErrorHandlingMode, LoaderHandlingMode } from './models';

export interface ForfettappApiClientOptions {
    cacheData?: boolean;
    refreshData?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ForfettappApiClient {
    private _postLoading = new BehaviorSubject<boolean>(false);
    private _getLoading = new BehaviorSubject<boolean>(false);
    private _putLoading = new BehaviorSubject<boolean>(false);
    private _deleteLoading = new BehaviorSubject<boolean>(false);
    private _apiError = new Subject<CodeDescriptionError>();

    private _globalLoading$ = combineLatest([this._postLoading, this._getLoading, this._putLoading, this._deleteLoading]).pipe(
        map(([postLoading, getLoading, putLoading, deleteLoading]) => postLoading || getLoading || putLoading || deleteLoading)
    );

    constructor(private http: HttpClient) {}

    get deleteLoading() {
        return this._deleteLoading;
    }

    public get isLoading$() {
        return this._globalLoading$;
    }

    public get apiError() {
        return this._apiError.asObservable();
    }

    post<T>(
        url: string,
        body?: any | null,
        params?: HttpParams,
        errorHandling?: ErrorHandlingMode,
        loaderHandling?: LoaderHandlingMode,
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'
    ): Observable<T>;

    post<T>(data: {
        url: string;
        body?: any | null;
        params?: HttpParams;
        headers?: HttpHeaders;
        withCredentials?: boolean;
        reportProgress?: boolean;
        loaderHandling?: LoaderHandlingMode;
        errorHandling?: ErrorHandlingMode;
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
    }): Observable<T>;

    post<T>(
        data:
            | {
                  url: string;
                  body?: any | null;
                  params?: HttpParams;
                  headers?: HttpHeaders;
                  withCredentials?: boolean;
                  reportProgress?: boolean;
                  loaderHandling?: LoaderHandlingMode;
                  errorHandling?: ErrorHandlingMode;
                  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
              }
            | string,
        body?: any | null,
        params?: HttpParams,
        errorHandling?: ErrorHandlingMode,
        loaderHandling?: LoaderHandlingMode,
        responseType: 'arraybuffer' | 'blob' | 'json' | 'text' = 'json'
    ): Observable<T> {
        if (typeof data === 'string') {
            // Overload con parametri (data = url)
            errorHandling = errorHandling || ErrorHandlingMode.useGlobalErrorHandler;
            loaderHandling = loaderHandling || LoaderHandlingMode.useGlobalLoaderHandler;

            if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                this._postLoading.next(true);
            }

            const completeUrl = data.startsWith('http') ? data : `${environment.coreApiUrl}${data}`;

            return this.http.request('POST', completeUrl, { body, params, responseType }).pipe(
                catchError(error =>
                    error instanceof HttpErrorResponse && errorHandling === ErrorHandlingMode.useGlobalErrorHandler
                        ? this.handleHttpErrorResponse(error)
                        : throwError(error)
                ),
                finalize(() => {
                    if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                        this._postLoading.next(false);
                    }
                })
            );
        }

        // Overload con oggetto (data = {url, params...})
        data.errorHandling = data.errorHandling || ErrorHandlingMode.useGlobalErrorHandler;
        data.loaderHandling = data.loaderHandling || LoaderHandlingMode.useGlobalLoaderHandler;

        if (data.loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
            this._postLoading.next(true);
        }

        const completeUrl = data.url.startsWith('http') ? data.url : `${environment.coreApiUrl}${data.url}`;

        return this.http
            .request('POST', completeUrl, {
                body: data.body ?? null,
                params: data.params ?? null,
                responseType: data.responseType ?? 'json',
                headers: data.headers,
            })
            .pipe(
                catchError(error =>
                    error instanceof HttpErrorResponse && data.errorHandling === ErrorHandlingMode.useGlobalErrorHandler
                        ? this.handleHttpErrorResponse(error)
                        : throwError(error)
                ),
                finalize(() => {
                    if (data.loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                        this._postLoading.next(false);
                    }
                })
            );
    }

    // post<T>(url: string, payload?: any, params?: HttpParams, errorHandling?: ErrorHandlingMode, loaderHandling?: LoaderHandlingMode) {
    //     errorHandling = errorHandling || ErrorHandlingMode.useGlobalErrorHandler;
    //     loaderHandling = loaderHandling || LoaderHandlingMode.useGlobalLoaderHandler;

    //     if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
    //         this._postLoading.next(true);
    //     }

    //     const completeUrl = url.startsWith('http') ? url : `${environment.coreApiUrl}${url}`;

    //     return this.http.post<T>(completeUrl, payload, { params }).pipe(
    //         catchError(error =>
    //             error instanceof HttpErrorResponse && errorHandling === ErrorHandlingMode.useGlobalErrorHandler
    //                 ? this.handleHttpErrorResponse(error)
    //                 : throwError(error)
    //         ),
    //         finalize(() => {
    //             if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
    //                 this._postLoading.next(false);
    //             }
    //         })
    //     );
    // }

    put<T>(url: string, payload: any, params?: HttpParams, loaderHandling: LoaderHandlingMode = LoaderHandlingMode.useGlobalLoaderHandler) {
        if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
            this._putLoading.next(true);
        }

        const completeUrl = url.startsWith('http') ? url : `${environment.coreApiUrl}${url}`;

        return this.http.put<T>(completeUrl, payload, { params }).pipe(
            catchError(error => this.handleHttpErrorResponse(error)),
            finalize(() => {
                if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                    this._putLoading.next(false);
                }
            })
        );
    }

    delete<T>(url: string, loaderHandling: LoaderHandlingMode = LoaderHandlingMode.useGlobalLoaderHandler, errorHandling?: ErrorHandlingMode) {
        errorHandling = errorHandling || ErrorHandlingMode.useGlobalErrorHandler;

        if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
            this._deleteLoading.next(true);
        }

        const completeUrl = url.startsWith('http') ? url : `${environment.coreApiUrl}${url}`;

        return this.http.delete<T>(completeUrl).pipe(
            catchError(error =>
                error instanceof HttpErrorResponse && errorHandling === ErrorHandlingMode.useGlobalErrorHandler
                    ? this.handleHttpErrorResponse(error)
                    : throwError(error)
            ),
            finalize(() => {
                if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                    this._deleteLoading.next(false);
                }
            })
        );
    }

    get<T>(
        url: string,
        params?: HttpParams,
        errorHandling?: ErrorHandlingMode,
        loaderHandling?: LoaderHandlingMode,
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text'
    ): Observable<T>;

    get<T>(data: {
        url: string;
        params?: HttpParams;
        headers?: HttpHeaders;
        withCredentials?: boolean;
        reportProgress?: boolean;
        loaderHandling?: LoaderHandlingMode;
        errorHandling?: ErrorHandlingMode;
        observe?: 'body' | 'events' | 'response';
        responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
    }): Observable<T>;

    get<T>(
        data:
            | {
                  url: string;
                  params?: HttpParams;
                  headers?: HttpHeaders;
                  withCredentials?: boolean;
                  reportProgress?: boolean;
                  loaderHandling?: LoaderHandlingMode;
                  errorHandling?: ErrorHandlingMode;
                  observe?: 'body' | 'events' | 'response';
                  responseType?: 'arraybuffer' | 'blob' | 'json' | 'text';
              }
            | string,
        params?: HttpParams,
        errorHandling?: ErrorHandlingMode,
        loaderHandling?: LoaderHandlingMode,
        responseType: 'arraybuffer' | 'blob' | 'json' | 'text' = 'json'
    ): Observable<T> {
        if (typeof data === 'string') {
            // Overload con parametri (data = url)
            errorHandling = errorHandling || ErrorHandlingMode.useGlobalErrorHandler;
            loaderHandling = loaderHandling || LoaderHandlingMode.useGlobalLoaderHandler;

            if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                this._getLoading.next(true);
            }

            const completeUrl = data.startsWith('http') ? data : `${environment.coreApiUrl}${data}`;

            return this.http.request('GET', completeUrl, { params, responseType }).pipe(
                catchError(error =>
                    error instanceof HttpErrorResponse && errorHandling === ErrorHandlingMode.useGlobalErrorHandler
                        ? this.handleHttpErrorResponse(error)
                        : throwError(error)
                ),
                finalize(() => {
                    if (loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                        this._getLoading.next(false);
                    }
                })
            );
        }

        // Overload con oggetto (data = {url, params...})
        data.errorHandling = data.errorHandling || ErrorHandlingMode.useGlobalErrorHandler;
        data.loaderHandling = data.loaderHandling || LoaderHandlingMode.useGlobalLoaderHandler;

        if (data.loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
            this._getLoading.next(true);
        }

        const completeUrl = data.url.startsWith('http') ? data.url : `${environment.coreApiUrl}${data.url}`;

        return this.http
            .request('GET', completeUrl, {
                params: data.params ?? null,
                responseType: data.responseType ?? 'json',
                headers: data.headers,
                observe: data.observe ?? 'body',
            })
            .pipe(
                catchError(error =>
                    error instanceof HttpErrorResponse && data.errorHandling === ErrorHandlingMode.useGlobalErrorHandler
                        ? this.handleHttpErrorResponse(error)
                        : throwError(error)
                ),
                finalize(() => {
                    if (data.loaderHandling === LoaderHandlingMode.useGlobalLoaderHandler) {
                        this._getLoading.next(false);
                    }
                })
            );
    }

    protected handleHttpErrorResponse(response: HttpErrorResponse) {
        console.warn('RAWErrorResponse', response);

        let errorCode: CodeDescriptionError;
        let responseErrorDetails: ErrorDetails;

        if (response.error instanceof ArrayBuffer) {
            responseErrorDetails = JSON.parse(new TextDecoder().decode(response.error));
        } else if (response.error == null) {
            responseErrorDetails = null;
        } else if (typeof response.error === 'object') {
            responseErrorDetails = response.error;
        } else {
            try {
                responseErrorDetails = JSON.parse(response.error);
            } catch {
                responseErrorDetails = null;
            }
        }

        switch (response.status) {
            case 0:
                errorCode = {
                    code: '0',
                    description: {
                        exception: '',
                        message: response.statusText,
                    },
                    internalErrors: [],
                    showModal: true,
                };
                break;

            case 400:
                var message = getErrorMessage(responseErrorDetails, 'Richiesta non valida');

                errorCode = {
                    code: '400',
                    description: { exception: '', message: message },
                    internalErrors: [],
                    showModal: true,
                };

                break;
            case 401:
                errorCode = {
                    code: '401',
                    description: {
                        exception: '',
                        message: getErrorMessage(responseErrorDetails, 'Unauthorized'),
                    },
                    internalErrors: [],
                    showModal: true,
                };

                break;
            case 403:
                errorCode = {
                    code: '403',
                    description: {
                        exception:
                            "L'accesso a questa pagina potrebbe essere stato negato oppure la pagina richiede il caricamento di dati attualmente non accessibili",
                        message: 'Autorizzazione negata',
                    },
                    internalErrors: [],
                    showModal: true,
                };
                break;
            case 404:
                errorCode = {
                    code: '404',
                    description: {
                        exception: '',
                        message: 'Elemento non trovato',
                    },
                    internalErrors: [],
                    showModal: true,
                };
                break;
            case 405:
                errorCode = {
                    code: '405',
                    description: {
                        exception: '',
                        message: 'Azione non permessa',
                    },
                    internalErrors: [],
                    showModal: false,
                };
                break;

            case 409:
                errorCode = {
                    code: '409',
                    description: { exception: '', message: 'Conflitto' },
                    internalErrors: [],
                    showModal: false,
                };
                break;

            case 422:
                errorCode = {
                    code: '422',
                    description: {
                        exception: '',
                        message: getErrorMessage(responseErrorDetails, 'Wrong Payload'),
                    },
                    internalErrors: response.error.errors ? Object.values(response.error.errors) : [],
                    showModal: true,
                };
                break;
            case 500:
                errorCode = {
                    code: '500',
                    description: {
                        exception: '',
                        message: 'Si è verificato un errore interno',
                    },
                    internalErrors: [],
                    showModal: true,
                };
                break;
            default: {
                if (response.error && response.error.type === 'error' && response.error.total === 0) {
                    errorCode = {
                        code: 'offline',
                        description: {
                            exception: '',
                            message: 'Server non raggiungibile',
                        },
                        internalErrors: [],
                        showModal: true,
                    };
                    errorCode.internalErrors.push('WARNING! You are offline!');
                } else {
                    errorCode = {
                        code: 'not_managed',
                        description: { exception: '', message: 'error' },
                        internalErrors: [],
                        showModal: false,
                    };
                }
            }
        }

        this._apiError.next(errorCode);
        return throwError(errorCode);
    }
}

export function errorDetailsAsString(error: ErrorDetails) {
    const data = error?.errors ?? error?.['#error'];
    if (!data) return null;
    try {
        let result: string = '';
        for (const m in data) {
            const arr = data[m];
            result = result + arr.toString() + '\n';
        }
        return result;
    } catch (e) {
        return null;
    }
}

export const getErrorMessage = (error: ErrorDetails, defaultMessage: string = 'Si è verificato un problema. Contatta il supporto tecnico.') =>
    errorDetailsAsString(error) ?? defaultMessage;

export interface ErrorDetails {
    status: number;
    title?: string;
    detail?: string;
    errors?: { [key: string]: string[] };
    '#error'?: { [key: string]: string[] };
}
